if (!customElements.get("variant-options")) {
  customElements.define(
    "variant-options",
    class VariantOptions extends HTMLElement {
      whenLoaded = Promise.all([customElements.whenDefined("gallery-section")]);

      constructor() {
        super();
        this.handleVariantChange = this.onVariantChange.bind(this);
        this.handleKeyDown = this.onKeyDown.bind(this);
        this.addEventListener("change", this.handleVariantChange);
        this.addEventListener("keydown", this.handleKeyDown);

        this.container = document.querySelector(
          `section[data-product-handle="${this.getAttribute("data-product-handle")}"]`,
        );
      }

      connectedCallback() {
        this.whenLoaded.then(() => {
          this.initialize();
        });
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.handleVariantChange);
        this.removeEventListener("keydown", this.handleKeyDown);
        this.variantData = null;
        this.currentVariant = null;
      }

      initialize() {
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.updateVariantStatuses();
        this.updateDropdownButtons();
      }

      onKeyDown(event) {
        if (event.key === "Enter" || event.keyCode === 13) {
          const input = event.target.querySelector("input");
          input?.click();
          // this.onVariantChange();
        }
      }

      onVariantChange() {
        this.emitCustomEvent("variantChangeStart");

        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.toggleAddButton(true, "", false);
        this.updatePickupAvailability();
        this.removeErrorMessage();
        this.updateVariantStatuses();
        this.updateDropdownButtons();

        if (!this.currentVariant) {
          this.toggleAddButton(true, "", true);
          this.setUnavailable();
        } else {
          this.updateMedia();
          if (document.querySelectorAll("variant-options").length === 1) {
            this.updateURL();
          }
          this.updateVariantInput();
          this.renderProductInfo();
          this.updateShareUrl();
        }

        this.emitCustomEvent("variantChangeEnd");
      }

      emitCustomEvent(eventName) {
        const event = new CustomEvent(eventName, { bubbles: true, composed: true });
        this.dispatchEvent(event);
      }

      updateGallery() {
        const mediaGallery = document.getElementById(
          `MediaGallery-${this.dataset.section}`,
        );
        if (!mediaGallery) return;

        const mediaId = this.currentVariant?.featured_media?.id ?? false;
        try {
          mediaGallery.filterSlides(this.options, mediaId, true);
        } catch (error) {
          console.error('Error updating gallery:', error);
        }
      }

      getFieldsets() {
        return Array.from(this.querySelectorAll(".wt-product__option"));
      }

      getCheckedInputValue(fieldset) {
        return fieldset.querySelector("input:checked")?.value || "";
      }

      getCheckedInput(fieldset) {
        return fieldset.querySelector("input:checked") || null;
      }

      updateDropdownButtons() {
        this.getFieldsets().forEach((fieldset) => {
          const dropdown = fieldset.querySelector(".wt-product__option__dropdown");
          const checkedInput = this.getCheckedInput(fieldset);

          if (!dropdown || !checkedInput) return;

          const isDisabled = checkedInput.classList.contains("disabled");
          dropdown.classList.toggle("wt-product__option__dropdown--unavailable", isDisabled);
        });
      }

      updateOptions() {
        const fieldsets = this.getFieldsets();

        this.options = fieldsets.map(fieldset => this.getCheckedInputValue(fieldset));
        this.checkedOptions = fieldsets.map(fieldset => this.getCheckedInput(fieldset));

        fieldsets.forEach((fieldset, index) => {
          const selectedOption = this.options[index];
          if (!selectedOption) return;

          this.updateOptionDisplay(fieldset, selectedOption);
          this.updateSliderSelection(fieldset);
        });
      }

      updateOptionDisplay(fieldset, selectedOption) {
        const titleValue = fieldset.querySelector(".wt-product__option__title .value");
        if (titleValue) titleValue.innerHTML = selectedOption;

        const dropdownSpan = fieldset.querySelector(".wt-product__option__dropdown span");
        if (dropdownSpan) dropdownSpan.innerHTML = selectedOption;
      }

      updateSliderSelection(fieldset) {
        if (!fieldset.closest(".card-variants-slider")) return;

        const checkedInput = this.getCheckedInput(fieldset);
        const allLabels = fieldset.querySelectorAll("label");

        allLabels.forEach(label => label.classList.remove("selected"));

        if (checkedInput) {
          const checkedLabel = fieldset.querySelector(`label[for="${checkedInput.id}"]`);
          checkedLabel?.classList.add("selected");
        }
      }

      updateMasterId() {
        this.currentVariant = this.getVariantData()?.find(variant =>
          variant.options.every((option, index) => this.options[index] === option)
        );
      }

      updateMedia() {
        if (!this.currentVariant?.featured_media) return;

        this.setAttribute("data-variant-id", this.currentVariant.id);
        this.setAttribute("data-featured-image", this.currentVariant.featured_media.preview_image?.src);
        this.setAttribute("data-featured-image-id", this.currentVariant.featured_media.id);

        this.reorderModalMedia();
      }

      reorderModalMedia() {
        const modalContent = document.querySelector(
          `#ProductModal-${this.dataset.section} .product-media-modal__content`,
        );
        if (!modalContent) return;

        const newMediaModal = modalContent.querySelector(
          `[data-media-id="${this.currentVariant.featured_media.id}"]`,
        );
        if (newMediaModal) modalContent.prepend(newMediaModal);
      }

      getSectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${this.dataset.url}?variant=${this.currentVariant.id}`,
        );
      }

      updateShareUrl() {
        const shareButton = document.getElementById(`Share-${this.getSectionId()}`);
        if (!shareButton?.updateUrl) return;
        shareButton.updateUrl(
          `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`,
        );
      }

      updateVariantInput() {
        this.updateFormInputs();
        this.updateCardFormInputs();
      }

      updateFormInputs() {
        const forms = document.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        );
        forms.forEach(form => this.setFormVariantId(form));
      }

      updateCardFormInputs() {
        const cardContainer = this.closest(".card__container");
        if (!cardContainer) return;

        const cardForms = cardContainer.querySelectorAll(
          'form[data-type="add-to-cart-form"] input[name="id"]',
        );
        cardForms.forEach(input => this.setFormVariantId(input));
      }

      setFormVariantId(element) {
        const input = element.name ? element : element.querySelector('input[name="id"]');
        if (!input) return;

        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }

      updateVariantStatuses() {
        const availableVariants = this.getVariantData()?.filter(v => v.available) || [];
        const optionWrappers = Array.from(this.querySelectorAll(".product-form__input"));
        const checkedInputValues = this.getCheckedInputValues();

        optionWrappers.forEach((wrapper, index) => {
          if (index === 0 && this.currentVariant?.options.length > 1) return;

          const optionInputs = Array.from(
            wrapper.querySelectorAll('input[type="radio"], option')
          );
          const previousOptionValue = optionWrappers[index - 1]?.querySelector(":checked")?.value;
          const availableValues = this.getAvailableOptionValues(
            availableVariants,
            index,
            checkedInputValues,
            previousOptionValue
          );

          this.updateInputAvailability(optionInputs, availableValues, availableVariants);
        });
      }

      getCheckedInputValues() {
        return Array.from(this.querySelectorAll(".product-form__input :checked")).map(
          input => input.getAttribute("value")
        );
      }

      getAvailableOptionValues(availableVariants, index, checkedValues, previousValue) {
        return availableVariants
          .filter(variant => this.variantMatchesPreviousOptions(variant, index, checkedValues, previousValue))
          .map(variant => variant[`option${index + 1}`]);
      }

      variantMatchesPreviousOptions(variant, index, checkedValues, previousValue) {
        if (index === 2) {
          return variant.available &&
            variant.option1 === checkedValues[0] &&
            variant.option2 === checkedValues[1];
        }
        return variant.available && variant[`option${index}`] === previousValue;
      }

      updateInputAvailability(optionInputs, availableValues, availableVariants) {
        optionInputs.forEach(input => {
          const value = input.getAttribute("value");
          const isAvailable = availableValues.includes(value);
          const existsInVariants = availableVariants.some(v => v.options.includes(value));

          input.classList.toggle("disabled", !isAvailable || !existsInVariants);
        });
      }

      updatePickupAvailability() {
        const pickUpAvailability = document.querySelector(
          "pickup-availability",
        );
        if (!pickUpAvailability) return;

        pickUpAvailability.dataset.variantId = this.currentVariant?.id;
        if (this.currentVariant && this.currentVariant.available) {
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
          pickUpAvailability.removeAttribute("available");
          pickUpAvailability.innerHTML = "";
        }
      }

      removeErrorMessage() {
        const productForm = this.closest("section")?.querySelector("product-form");
        try {
          productForm?.handleErrorMessage?.();
        } catch (error) {
          console.error('Error removing error message:', error);
        }
      }

      renderProductInfo() {
        const requestedVariantId = this.currentVariant?.id;
        const sectionId = this.getSectionId();

        fetch(this.buildProductInfoUrl(requestedVariantId, sectionId))
          .then(response => response.text())
          .then(responseText => this.handleProductInfoResponse(responseText, requestedVariantId, sectionId))
          .catch(error => console.error('Error rendering product info:', error));
      }

      buildProductInfoUrl(variantId, sectionId) {
        return `${this.dataset.url}?variant=${variantId}&section_id=${sectionId}`;
      }

      handleProductInfoResponse(responseText, requestedVariantId, sectionId) {
        if (this.currentVariant?.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, "text/html");

        this.updateProductElements(html, sectionId);
        this.publishVariantChangeEvent(sectionId, html);
      }

      updateProductElements(html, sectionId) {
        const elements = {
          price: { src: `price-${this.getSectionId()}`, dest: `price-${this.dataset.section}` },
          sku: { src: `Sku-${this.getSectionId()}`, dest: `Sku-${this.dataset.section}` },
          inventory: { src: `Inventory-${this.getSectionId()}`, dest: `Inventory-${this.dataset.section}` },
          omnibus: { src: `jkl-pdp-omnibus-${this.getSectionId()}`, dest: `jkl-pdp-omnibus-${this.dataset.section}` }
        };

        Object.values(elements).forEach(({ src, dest }) => {
          this.syncElement(html, src, dest);
        });

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(
          addButtonUpdated?.hasAttribute("disabled") ?? true,
          window.variantStrings.soldOut,
        );
      }

      syncElement(html, sourceId, destId) {
        const source = html.getElementById(sourceId);
        const destination = document.getElementById(destId);

        if (!source || !destination) return;

        destination.innerHTML = source.innerHTML;

        if (sourceId.includes("visibility")) {
          destination.classList.toggle(
            "visibility-hidden",
            source.classList.contains("visibility-hidden")
          );
        }
      }

      publishVariantChangeEvent(sectionId, html) {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant
          }
        });
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = addButton?.querySelector('span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          if (addButtonText) addButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      setUnavailable() {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = addButton?.querySelector('span');
        if (!addButton || !addButtonText) return;

        addButtonText.textContent = window.variantStrings.unavailable;

        this.toggleVisibility(
          [
            `price-${this.dataset.section}`,
            `Inventory-${this.dataset.section}`,
            `Sku-${this.dataset.section}`
          ],
          true
        );
      }

      toggleVisibility(elementIds, hide) {
        elementIds.forEach(id => {
          const element = document.getElementById(id);
          if (element) {
            element.classList.toggle("visibility-hidden", hide);
          }
        });
      }

      getVariantData() {
        if (this.variantData) return this.variantData;

        try {
          const scriptElement = this.querySelector('[type="application/json"]');
          if (!scriptElement) {
            console.error('Variant data script element not found');
            return [];
          }
          this.variantData = JSON.parse(scriptElement.textContent);
        } catch (error) {
          console.error('Error parsing variant data:', error);
          this.variantData = [];
        }
        return this.variantData;
      }
    },
  );
}
// customElements.define('variant-options', VariantOptions);
